  import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { getCustomers, blockCustomer, unblockCustomer, deleteCustomer } from '@/services/customers'

export function useCustomers() {
  const customers = ref([])
  const loading = ref(false)
  const searchQuery = ref('')
  const currentPage = ref(1)
  const totalPages = ref(1)
  const totalCustomers = ref(0)
  // Ajustamos este valor para que coincida con el PAGE_SIZE del backend (24)
  // Este valor NUNCA debe cambiar dinámicamente para mantener coherencia en la paginación
  const itemsPerPage = ref(24)
  
  // Variable para debugging - Detectar el tamaño real de la página que usa el backend
  const detectedPageSize = ref(0)

  // Filtros adicionales
  const roleFilter = ref('')
  const customerTypeFilter = ref('')
  const staffFilter = ref('')
  
  const router = useRouter()
  const toast = useToast()
  
  const filteredCustomers = computed(() => {
    console.log('Current customers:', customers.value)
    if (!Array.isArray(customers.value)) return []
    if (!searchQuery.value) return customers.value
    
    const query = searchQuery.value.toLowerCase()
    return customers.value.filter(customer => {
      return (customer.username && customer.username.toLowerCase().includes(query)) ||
        (customer.email && customer.email.toLowerCase().includes(query)) ||
        (customer.dni && customer.dni.toLowerCase().includes(query))
    })
  })
    const fetchCustomers = async () => {
    try {
      loading.value = true
      console.log(`Fetching customers: page ${currentPage.value}, search: "${searchQuery.value}", using itemsPerPage=${itemsPerPage.value}`)
      
      const response = await getCustomers({
        page: currentPage.value,
        search: searchQuery.value,
        ...(roleFilter.value ? { role: roleFilter.value } : {}),
        ...(customerTypeFilter.value ? { customer_type: customerTypeFilter.value } : {}),
        ...(staffFilter.value ? { is_staff: staffFilter.value } : {})
      })
      
      // Verificar la estructura de respuesta
      let customersData = [];
      
      if (Array.isArray(response)) {
        customersData = response;
      } else if (response && Array.isArray(response.results)) {
        // La respuesta está paginada
        customersData = response.results;
        
        // Actualizar información de paginación
        if (response.count !== undefined) {
          totalCustomers.value = response.count;
          
          // Solo para debugging: detectar el tamaño real de la página que usa el backend
          if (customersData.length > 0) {
            detectedPageSize.value = customersData.length;
          }
          
          // Usamos el valor fijo de itemsPerPage sin ajustarlo dinámicamente
          // Asegurarse de que itemsPerPage.value sea al menos 1 para evitar división por cero
          const effectiveItemsPerPage = Math.max(1, itemsPerPage.value);
          
          // Calcular total de páginas
          totalPages.value = Math.ceil(response.count / effectiveItemsPerPage) || 1;
          
          console.log(`Total customers: ${totalCustomers.value}, Items per page: ${effectiveItemsPerPage}, Detected page size: ${detectedPageSize.value}, Pages: ${totalPages.value}`);
        }
      } else if (response && typeof response === 'object') {
        // Si es un solo objeto, lo convertimos en array
        customersData = [response];
      } else {
        console.error('Invalid response format:', response);
        customers.value = [];
        totalPages.value = 1;
        totalCustomers.value = 0;
        return;
      }
      
      // Transformar los datos para el formato esperado
      customers.value = customersData.map(customer => ({
        id: customer.id,
        username: customer.username || '',
        email: customer.email || '',
        dni: customer.dni || '',
        is_active: customer.is_active !== undefined ? customer.is_active : true,
        is_staff: customer.is_staff || false,
        total_orders: customer.total_orders || 0,
        total_spent: customer.total_spent || 0,
        last_purchase: customer.last_purchase,
        date_joined: customer.date_joined,
        name: customer.name || customer.username,
        admin_notes: customer.admin_notes || '',
        customer_type: customer.customer_type || 'REGULAR',
        distributor_pending_approval: Boolean(customer.distributor_pending_approval),
        phone: customer.phone || '',
        business_name: customer.business_name || '',
        instagram: customer.instagram || '',
        roles: customer.roles || []
      }))
      
      // Si no recibimos información de paginación explícita
      if (!response || !response.count) {
        // Actualizar el contador de clientes
        totalCustomers.value = customersData.length || 0;
        
        // Si no hay paginación, significa que todos los resultados están en una sola página
        if (!response?.next && !response?.previous) {
          totalPages.value = 1;
        } 
        // Si hay paginación pero no count, intentamos calcular basado en los datos disponibles
        else {
          // Como no tenemos count total, asumimos que hay al menos una página más si hay "next"
          totalPages.value = response?.next ? currentPage.value + 1 : currentPage.value;
        }
        
        console.log(`Sin paginación explícita - Total customers: ${totalCustomers.value}, Pages: ${totalPages.value}`);
      }
      
      return response
    } catch (error) {
      console.error('Error fetching customers:', error)
      toast.error('Error al cargar la lista de clientes')
      customers.value = []
      totalPages.value = 1
      totalCustomers.value = 0
    } finally {
      loading.value = false
    }
  }
  
  const viewCustomerOrders = async (customer) => {
    router.push({
      name: 'AdminSalesHistory',
      query: { 
        search: customer.email,
        customer_id: customer.id.toString()
      }
    })
  }
  
  const handleBlockCustomer = async (customer, adminNotes = '') => {
    try {
      const response = await blockCustomer(customer.id, adminNotes)
      customer.is_active = false
      customer.admin_notes = response.admin_notes || adminNotes
      toast.success('Cliente bloqueado correctamente')
      await fetchCustomers()
    } catch (error) {
      console.error('Error blocking customer:', error)
      toast.error('Error al bloquear el cliente')
    }
  }
  
  const handleUnblockCustomer = async (customer, adminNotes = '') => {
    try {
      const response = await unblockCustomer(customer.id, adminNotes)
      customer.is_active = true
      customer.admin_notes = response.admin_notes || adminNotes
      toast.success('Cliente desbloqueado correctamente')
      await fetchCustomers()
    } catch (error) {
      console.error('Error unblocking customer:', error)
      toast.error('Error al desbloquear el cliente')
    }
  }
  
  const handleDeleteCustomer = async (customer) => {
    try {
      await deleteCustomer(customer.id)
      toast.success('Cliente eliminado correctamente')
      await fetchCustomers()
    } catch (error) {
      console.error('Error deleting customer:', error)
      toast.error(error.message || 'Error al eliminar el cliente')
    }
  }
  
  // Watch for page changes
  watch(currentPage, () => {
    console.log('Page changed, fetching customers...')
    fetchCustomers()
  })
  
  // Watch for filter changes - reset to page 1
  watch([roleFilter, customerTypeFilter, staffFilter], () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }
    fetchCustomers()
  })

  // Watch for search changes
  watch(searchQuery, () => {
    console.log('Search changed, fetching customers...')
    // Reset to page 1 when searching
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return // No need to fetch, the currentPage watcher will do it
    }
    fetchCustomers()
  })
  
  // Función para validar la paginación y asegurar que no se navegue a páginas inexistentes
  const validatePagination = () => {
    // Asegurar que currentPage no sea mayor que totalPages
    if (currentPage.value > totalPages.value) {
      console.warn(`La página ${currentPage.value} no existe, redirigiendo a la página ${totalPages.value}`)
      currentPage.value = totalPages.value
    }
    // Asegurar que currentPage no sea menor que 1
    else if (currentPage.value < 1) {
      console.warn('La página no puede ser menor que 1, redirigiendo a la página 1')
      currentPage.value = 1
    }
  }
  
  // Validar paginación cuando cambia totalPages
  watch(totalPages, validatePagination)
  
  // Inicializar con validación de paginación para evitar estados inconsistentes
  validatePagination()
  
  return {
    customers,
    loading,
    searchQuery,
    currentPage,
    totalPages,
    totalCustomers,
    itemsPerPage,
    detectedPageSize,
    filteredCustomers,
    fetchCustomers,
    viewCustomerOrders,
    handleBlockCustomer,
    handleUnblockCustomer,
    handleDeleteCustomer,
    validatePagination,
    roleFilter,
    customerTypeFilter,
    staffFilter
  }
} 